import React, { useState, useRef } from 'react';
import './CreateBlog.css';
import SummaryApi from '../../../components/common/index';
import { 
  FiType, FiVideo, FiBold, FiItalic,
  FiAlignLeft, FiAlignCenter, FiAlignRight, 
  FiList, FiLink2, FiSave, FiHash, FiGlobe, FiImage, FiCheck
} from 'react-icons/fi';

const CreateBlog = () => {
  const [formData, setFormData] = useState({
    blog_title: '',
    blog_subtitle: '',
    blog_content: '',
    tags: '',
    url: ''
  });

  const [files, setFiles] = useState({
    blog_thumbnail: null,
    cover_image: null,
    blog_video: null
  });

  const [previews, setPreviews] = useState({
    thumbnail: '',
    cover: '',
    videoUrl: '',
    thumbName: '',
    coverName: '',
    videoName: ''
  });

  const [loading, setLoading] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const textareaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const { name, files: selected } = e.target;
    if (!selected?.[0]) return;

    const file = selected[0];
    const url = URL.createObjectURL(file);

    setFiles(prev => ({ ...prev, [name]: file }));
    
    if (name === 'blog_thumbnail') setPreviews(prev => ({ ...prev, thumbnail: url, thumbName: file.name }));
    if (name === 'cover_image') setPreviews(prev => ({ ...prev, cover: url, coverName: file.name }));
    if (name === 'blog_video') setPreviews(prev => ({ ...prev, videoUrl: url, videoName: file.name }));
  };

  const insertTag = (before, after = "") => {
    const ta = textareaRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = formData.blog_content;
    const selected = text.slice(start, end) || "text";
    
    const newText = text.slice(0, start) + before + selected + after + text.slice(end);
    setFormData(prev => ({ ...prev, blog_content: newText }));

    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 10);
  };

  const format = (type) => {
    switch (type) {
      case 'p': insertTag('<p>', '</p>'); break; // New Paragraph Logic
      case 'h1': insertTag('<h1>', '</h1>'); break;
      case 'h2': insertTag('<h2>', '</h2>'); break;
      case 'h3': insertTag('<h3>', '</h3>'); break;
      case 'bold': insertTag('<strong>', '</strong>'); break;
      case 'italic': insertTag('<em>', '</em>'); break;
      case 'left': insertTag('<div className="align-left">', '</div>'); break;
      case 'center': insertTag('<div className="align-center">', '</div>'); break;
      case 'right': insertTag('<div className="align-right">', '</div>'); break;
      case 'list': insertTag('<ul>\n  <li>', '</li>\n</ul>'); break;
      case 'link': 
        const userUrl = prompt("Enter the full URL (including http/https):", "https://");
        if (userUrl && userUrl !== "https://") {
          insertTag(`<a href="${userUrl}" target="_blank" rel="noopener noreferrer">`, '</a>');
        }
        break;
      default: break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(!formData.blog_title) return alert("Please add a title");
    
    setLoading(true);
    setIsPublished(false);

    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    if (files.blog_thumbnail) data.append('blog_thumbnail', files.blog_thumbnail);
    if (files.cover_image)    data.append('cover_image', files.cover_image);
    if (files.blog_video)     data.append('blog_video', files.blog_video);

    try {
      const res = await fetch(SummaryApi.createBlog.url, {
        method: SummaryApi.createBlog.method,
        body: data,
      });
      const json = await res.json();

      if (json.status) {
        setLoading(false);      
        setIsPublished(true);   

        setTimeout(() => {
          alert('Blog published successfully!');
          window.location.reload(); 
        }, 150); 
      } else {
        setLoading(false);
        alert(json.message || "Something went wrong");
      }
    } catch (err) {
      setLoading(false);
      alert('Upload failed. Please check your network.');
    }
  };

  return (
    <div className="admin-editor-layout">
      {/* --- EDITOR PANEL --- */}
      <aside className="editor-container">
        <div className="editor-top-bar">
          <div className="brand">
            <FiType className="brand-icon" />
            <span>Create Blog</span>
          </div>
          <button 
            className={`pub-button ${loading ? 'is-loading' : ''} ${isPublished ? 'is-published' : ''}`} 
            onClick={handleSubmit} 
            disabled={loading || isPublished}
          >
            {loading ? (
              <div className="spinner-small" />
            ) : isPublished ? (
              <><FiCheck /> Published!</>
            ) : (
              <><FiSave /> Publish Post</>
            )}
          </button>
        </div>

        <div className="editor-fields">
          <div className="input-header-group">
            <input 
              className="title-field" 
              name="blog_title" 
              placeholder="Post Title..." 
              value={formData.blog_title} 
              onChange={handleChange} 
            />
            <input 
              className="subtitle-field" 
              name="blog_subtitle" 
              placeholder="Add a catchy subtitle..." 
              value={formData.blog_subtitle} 
              onChange={handleChange} 
            />
          </div>

          <div className="editor-toolbar">
            <div className="t-group">
              <button title="Paragraph" onClick={() => format('p')}>P</button>
              <button title="Heading 1" onClick={() => format('h1')}>H1</button>
              <button title="Heading 2" onClick={() => format('h2')}>H2</button>
              <button title="Heading 3" onClick={() => format('h3')}>H3</button>
            </div>
            <div className="t-group">
              <button title="Bold" onClick={() => format('bold')}><FiBold /></button>
              <button title="Italic" onClick={() => format('italic')}><FiItalic /></button>
              <button title="Link" onClick={() => format('link')}><FiLink2 /></button>
              <button title="List" onClick={() => format('list')}><FiList /></button>
            </div>
            <div className="t-group">
              <button title="Align Left" onClick={() => format('left')}><FiAlignLeft /></button>
              <button title="Align Center" onClick={() => format('center')}><FiAlignCenter /></button>
              <button title="Align Right" onClick={() => format('right')}><FiAlignRight /></button>
            </div>
          </div>

          <textarea 
            ref={textareaRef}
            className="main-textarea"
            name="blog_content"
            placeholder="Start your story here..."
            value={formData.blog_content}
            onChange={handleChange}
          />

          <div className="meta-grid">
            <div className="m-input">
              <FiHash />
              <input name="tags" placeholder="Tags (comma separated)" value={formData.tags} onChange={handleChange} />
            </div>
            <div className="m-input">
              <FiGlobe />
              <input name="url" placeholder="Source URL" value={formData.url} onChange={handleChange} />
            </div>
          </div>

          <div className="upload-section">
            <label className="u-box">
              <FiImage />
              <span>Cover Image</span>
              <p>{previews.coverName || 'Upload Image'}</p>
              <input type="file" name="cover_image" hidden onChange={handleFile} accept="image/*" />
            </label>
            <label className="u-box">
              <FiType />
              <span>Thumbnail</span>
              <p>{previews.thumbName || 'Upload Thumbnail'}</p>
              <input type="file" name="blog_thumbnail" hidden onChange={handleFile} accept="image/*" />
            </label>
            <label className="u-box">
              <FiVideo />
              <span>Blog Video</span>
              <p>{previews.videoName || 'Optional Video'}</p>
              <input type="file" name="blog_video" hidden onChange={handleFile} accept="video/*" />
            </label>
          </div>
        </div>
      </aside>

      {/* --- PREVIEW PANEL --- */}
      <main className="preview-container">
        <div className="preview-card">
          <div className="preview-browser-top">
            <div className="dots"><span /><span /><span /></div>
            <div className="preview-label">Live Preview</div>
          </div>
          
          <div className="preview-scroll-view">
            <div className="p-header">
              {previews.cover ? (
                <img src={previews.cover} alt="Cover" className="p-cover" />
              ) : (
                <div className="p-cover-empty">No Cover Image Selected</div>
              )}
              
              {previews.thumbnail && (
                <div className="p-thumb">
                  <img src={previews.thumbnail} alt="Thumbnail" />
                </div>
              )}
            </div>

            <div className="p-body">
              <div className="p-meta-top">
                <span className="p-status-badge">Preview Mode</span>
                <div className="p-tags">
                  {formData.tags ? formData.tags.split(',').map((t, i) => (
                    <span key={i} className="tag-pill">#{t.trim()}</span>
                  )) : <span className="tag-pill">#tags</span>}
                </div>
              </div>

              <h1 className="p-title">{formData.blog_title || "Article Title Placeholder"}</h1>
              <p className="p-subtitle">{formData.blog_subtitle || "Catchy subtitle will appear here..."}</p>

              <div className="p-divider" />

              {/* THIS DIV RENDERS THE HTML CONTENT INCLUDING <P> TAGS */}
              <div 
                className="p-content-render"
                dangerouslySetInnerHTML={{ __html: formData.blog_content || "<p>Your content will render here...</p>" }}
              />

              {previews.videoUrl && (
                <div className="p-video-box">
                  <div className="video-meta"><FiVideo /> Attached Video</div>
                  <video src={previews.videoUrl} controls />
                </div>
              )}
              
              {formData.url && (
                <div className="p-source-link">
                  <FiGlobe /> <strong>Source:</strong> <a href={formData.url} target="_blank" rel="noreferrer">{formData.url}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreateBlog;